import { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { PageHeader, EmptyState } from "@/components/common";
import { Plus, Edit, Trash2, Loader2, ArrowDown, GitBranch, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm";

export default function Workflows() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [users, setUsers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/approval-workflows");
      setRows(r.data?.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/users?limit=200").then((r) => setUsers(r.data?.data || []));
    api.get("/masters/task-types").then((r) => setTaskTypes(r.data?.data || []));
    api.get("/masters/priorities").then((r) => setPriorities(r.data?.data || []));
    api.get("/departments").then((r) => setDepartments(r.data?.data || []));
    api.get("/teams").then((r) => setTeams(r.data?.data || []));
  }, []);

  const newWf = () => {
    setEditing(null);
    setForm({
      code: "", name: "",
      mode: "sequential",
      auto_escalation: true, rework_allowed: true, rejection_allowed: true,
      is_active: true,
      applicable_task_type_ids: [],
      applicable_priority_codes: [],
      applicable_department_ids: [],
      applicable_team_ids: [],
      applicable_assignee_ids: [],
      min_amount: "", max_amount: "",
      approval_levels: [{ level: 1, approver_id: "", approver_name: "", deadline_hours: 24 }],
    });
    setOpen(true);
  };

  const editWf = (r) => {
    setEditing(r);
    setForm({
      ...r,
      approval_levels: r.approval_levels || [],
      applicable_task_type_ids: r.applicable_task_type_ids || [],
      applicable_priority_codes: r.applicable_priority_codes || [],
      applicable_department_ids: r.applicable_department_ids || [],
      applicable_team_ids: r.applicable_team_ids || [],
      applicable_assignee_ids: r.applicable_assignee_ids || [],
      min_amount: r.min_amount ?? "",
      max_amount: r.max_amount ?? "",
    });
    setOpen(true);
  };

  const addLevel = () => {
    setForm((f) => ({
      ...f,
      approval_levels: [...(f.approval_levels || []), {
        level: (f.approval_levels?.length || 0) + 1,
        approver_id: "", approver_name: "", deadline_hours: 24,
      }],
    }));
  };

  const removeLevel = (i) => {
    setForm((f) => {
      const arr = [...(f.approval_levels || [])];
      arr.splice(i, 1);
      arr.forEach((l, idx) => (l.level = idx + 1));
      return { ...f, approval_levels: arr };
    });
  };

  const setLevelField = (i, k, v) => {
    setForm((f) => {
      const arr = [...(f.approval_levels || [])];
      arr[i] = { ...arr[i], [k]: v };
      if (k === "approver_id") {
        const u = users.find((x) => x.id === v);
        arr[i].approver_name = u?.name || "";
      }
      return { ...f, approval_levels: arr };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        applicable_task_type_ids: form.applicable_task_type_ids || [],
        applicable_priority_codes: form.applicable_priority_codes || [],
        applicable_department_ids: form.applicable_department_ids || [],
        applicable_team_ids: form.applicable_team_ids || [],
        applicable_assignee_ids: form.applicable_assignee_ids || [],
        min_amount: form.min_amount === "" || form.min_amount === null ? null : Number(form.min_amount),
        max_amount: form.max_amount === "" || form.max_amount === null ? null : Number(form.max_amount),
        approval_levels: (form.approval_levels || []).map((l) => ({
          level: l.level,
          approver_id: l.approver_id || null,
          approver_name: l.approver_name || null,
          deadline_hours: l.deadline_hours || 24,
        })),
      };
      if (editing) await api.put(`/approval-workflows/${editing.id}`, payload);
      else await api.post("/approval-workflows", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };

  const remove = async (r) => {
    if (!(await confirmDialog({ title: "Delete workflow", message: `Delete workflow "${r.name}"?`, confirmText: "Delete", danger: true }))) return;
    try { await api.delete(`/approval-workflows/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const SortTh = ({ label, k }) => (
    <th onClick={() => toggleSort(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );

  const displayed = useMemo(() => {
    let data = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(q)));
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
        const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir]);

  return (
    <div data-testid="workflows-page">
      <PageHeader
        title="Approval Workflows"
        breadcrumbs={["Transactions", "Workflows"]}
        actions={
          <button onClick={newWf} className="erp-btn-primary inline-flex items-center gap-2" data-testid="new-workflow-btn">
            <Plus size={14} /> New Workflow
          </button>
        }
      />

      <div className="erp-card p-3 mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search workflows..." className="erp-input pl-8 text-sm" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>
        <span className="text-xs text-slate-500">{displayed.length} results</span>
      </div>

      <div className="erp-card overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <EmptyState title="No workflows" hint="Create a multi-level approval workflow to chain approvers." />
        ) : (
          <table className="erp-table">
            <thead>
              <tr><SortTh label="Code" k="code"/><SortTh label="Name" k="name"/><SortTh label="Mode" k="mode"/><th>Levels</th><th>Conditions</th><SortTh label="Active" k="is_active"/><th></th></tr>
            </thead>
            <tbody>
              {displayed.map((r) => (
                <tr key={r.id} data-testid={`wf-row-${r.code}`}>
                  <td className="font-mono text-xs">{r.code}</td>
                  <td className="font-medium">{r.name}</td>
                  <td><span className="badge-base bg-slate-50 text-slate-700 border-slate-200">{r.mode}</span></td>
                  <td>
                    <div className="flex items-center gap-1">
                      <GitBranch size={12} className="text-slate-400" />
                      <span className="text-xs">{(r.approval_levels || []).length} level{(r.approval_levels || []).length !== 1 ? "s" : ""}</span>
                    </div>
                  </td>
                  <td className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {(r.applicable_priority_codes || []).map((p) => (
                        <span key={p} className="badge-base bg-blue-50 text-blue-700 border-blue-200">{p}</span>
                      ))}
                      {(r.min_amount || r.max_amount) && (
                        <span className="badge-base bg-emerald-50 text-emerald-700 border-emerald-200">
                          {r.min_amount ? `≥${r.min_amount}` : ""}{r.min_amount && r.max_amount ? "–" : ""}{r.max_amount ? `≤${r.max_amount}` : ""}
                        </span>
                      )}
                      {(!(r.applicable_priority_codes || []).length && !r.min_amount && !r.max_amount && !(r.applicable_task_type_ids || []).length) && (
                        <span className="text-slate-400">All tasks</span>
                      )}
                    </div>
                  </td>
                  <td>{r.is_active ? <span className="badge-base bg-emerald-50 text-emerald-700 border-emerald-200">Active</span> : <span className="badge-base bg-slate-100 text-slate-500 border-slate-200">Inactive</span>}</td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => editWf(r)} className="p-1.5 hover:bg-slate-100 rounded-sm" data-testid={`wf-edit-${r.code}`}><Edit size={14} /></button>
                      <button onClick={() => remove(r)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl" data-testid="workflow-form-dialog" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Workflow" : "New Approval Workflow"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Code *</label>
                  <input className="erp-input mt-1" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} required data-testid="wf-code-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name *</label>
                  <input className="erp-input mt-1" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="wf-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mode</label>
                  <select className="erp-input mt-1" value={form.mode || "sequential"} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="wf-mode-input">
                    <option value="sequential">Sequential</option>
                    <option value="parallel">Parallel</option>
                    <option value="any">Any (one approves)</option>
                    <option value="all">All (everyone approves)</option>
                  </select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.auto_escalation} onChange={(e) => setForm({ ...form, auto_escalation: e.target.checked })} /> Auto Escalation</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Conditions (when this workflow applies)</div>
                </div>
                <div className="grid grid-cols-2 gap-3 p-3 border border-slate-200 rounded-sm bg-slate-50">
                  <div className="col-span-2">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Task Types (empty = all)</label>
                    <div className="mt-1 flex flex-wrap gap-1.5 p-2 border border-slate-200 bg-white rounded-sm max-h-24 overflow-y-auto">
                      {taskTypes.length === 0 && <span className="text-[11px] text-slate-400">No task types</span>}
                      {taskTypes.map((t) => {
                        const checked = (form.applicable_task_type_ids || []).includes(t.id);
                        return (
                          <label key={t.id} className={`text-xs px-2 py-1 rounded-sm cursor-pointer border ${checked ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                            <input type="checkbox" className="hidden" checked={checked}
                              onChange={() => setForm((f) => ({
                                ...f,
                                applicable_task_type_ids: checked
                                  ? (f.applicable_task_type_ids || []).filter((x) => x !== t.id)
                                  : [...(f.applicable_task_type_ids || []), t.id],
                              }))} />
                            {t.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Priorities (empty = all)</label>
                    <div className="mt-1 flex flex-wrap gap-1.5 p-2 border border-slate-200 bg-white rounded-sm">
                      {priorities.length === 0 && <span className="text-[11px] text-slate-400">No priorities</span>}
                      {priorities.map((p) => {
                        const checked = (form.applicable_priority_codes || []).includes(p.code);
                        return (
                          <label key={p.code} className={`text-xs px-2 py-1 rounded-sm cursor-pointer border ${checked ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                            <input type="checkbox" className="hidden" checked={checked}
                              onChange={() => setForm((f) => ({
                                ...f,
                                applicable_priority_codes: checked
                                  ? (f.applicable_priority_codes || []).filter((x) => x !== p.code)
                                  : [...(f.applicable_priority_codes || []), p.code],
                              }))} data-testid={`priority-${p.code}`} />
                            <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: p.color }}></span>
                            {p.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Min Amount</label>
                    <input type="number" step="0.01" className="erp-input mt-1 text-sm" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} placeholder="leave blank for no min" data-testid="wf-min-amount" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Max Amount</label>
                    <input type="number" step="0.01" className="erp-input mt-1 text-sm" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} placeholder="leave blank for no max" data-testid="wf-max-amount" />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Approval Chain</div>
                  <button type="button" onClick={addLevel} className="erp-btn-secondary text-xs inline-flex items-center gap-1" data-testid="add-level-btn">
                    <Plus size={12} /> Add Level
                  </button>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto p-3 border border-slate-200 rounded-sm bg-slate-50">
                  {(form.approval_levels || []).length === 0 && <div className="text-xs text-slate-500">No levels yet</div>}
                  {(form.approval_levels || []).map((lvl, i) => (
                    <div key={i}>
                      <div className="flex items-start gap-2 bg-white border border-slate-200 p-3 rounded-sm" data-testid={`level-${lvl.level}`}>
                        <div className="w-7 h-7 rounded-sm bg-slate-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">L{lvl.level}</div>
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div data-testid={`level-${lvl.level}-approver`}>
                            <SearchableSelect
                              options={users.map(u => ({ value: u.id, label: `${u.name} · ${u.email}` }))}
                              value={lvl.approver_id || ""}
                              onValueChange={(v) => setLevelField(i, "approver_id", v)}
                              placeholder="Select approver"
                              allowClear
                            />
                          </div>
                          <input type="number" className="erp-input text-xs py-1.5" placeholder="Deadline (hrs)"
                            value={lvl.deadline_hours || ""} onChange={(e) => setLevelField(i, "deadline_hours", Number(e.target.value))} />
                        </div>
                        <button type="button" onClick={() => removeLevel(i)} className="p-1 text-red-600 hover:bg-red-50 rounded-sm" data-testid={`remove-level-${lvl.level}`}><Trash2 size={12} /></button>
                      </div>
                      {i < (form.approval_levels || []).length - 1 && (
                        <div className="flex justify-center py-1"><ArrowDown size={12} className="text-slate-400" /></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="erp-btn-secondary">Cancel</button>
                <button type="submit" className="erp-btn-primary" data-testid="save-workflow-btn">{editing ? "Update" : "Create"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
